import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {SelectComboboxList, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {forwardRef, useRef, useState} from 'react'
import {ComboboxInput, ComboboxItem, ComboboxPopover} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {TeamMember} from '@/features/treatment-requests/teams/types'
import {useTeamSearchableList} from '@/features/treatment-requests/teams/hooks/useTeamSearchableList'

interface InputSearchTeamMemberProps {
    treatmentRequestId: string
    disabled?: boolean
    label?: string
    value: Pick<TeamMember, 'id' | 'group' | 'user'> | Pick<TeamMember, 'id' | 'group' | 'user'>[] | null
    onChange: (
        value: Pick<TeamMember, 'id' | 'group' | 'user'> | Pick<TeamMember, 'id' | 'group' | 'user'>[] | null
    ) => void
    placeholder?: string
    errorMessage?: string
    projectId?: string
    disabledConsultants?: string[]
}

export const InputSearchTeamMember = forwardRef<HTMLInputElement, InputSearchTeamMemberProps>(
    (
        {
            treatmentRequestId,
            disabled,
            label,
            onChange,
            errorMessage,
            placeholder,
            value,
            projectId,
            disabledConsultants
        }: InputSearchTeamMemberProps,
        ref
    ) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const searchInputRef = useRef<HTMLInputElement>(null)

        const {teamMembers, isLoading, isError, hasNextPage, fetchNextPage, onSearch, onResetSearch} =
            useTeamSearchableList({
                treatmentRequestId,
                responseType: 'small',
                projectId: projectId ?? undefined,
                enabled: !disabled && isQueryEnabled
            })

        const combobox = useComboboxStore({
            placement: 'bottom-start',
            setOpen: open => setIsQueryEnabled(open),
            setValue: onSearch,
            selectedValue: Array.isArray(value) ? value.map(operator => operator.id) : value?.id || '',
            resetValueOnHide: true,
            setSelectedValue: (value: string | string[]) => {
                const values = Array.isArray(value) ? value : [value]
                const teamMembersFound: TeamMember[] = values.map(
                    id => teamMembers.find(teamMember => teamMember.id == id) as TeamMember
                )
                onChange(teamMembersFound)
            }
        })

        return (
            <Flexbox direction="column" gap={1.5} fullWidth>
                {label && <Label>{label}</Label>}
                <ComboboxProvider store={combobox}>
                    <ComboboxInput
                        ref={ref || searchInputRef}
                        hasCloseCombobox
                        placeholder={placeholder}
                        onClear={() => onResetSearch(searchInputRef)}
                        disabled={disabled}
                    />
                    <ComboboxPopover>
                        <SelectComboboxList>
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : isError ? (
                                <InputHelpText error={errorMessage || t('errors:default')} />
                            ) : teamMembers.length > 0 ? (
                                <SelectVirtualList
                                    data={teamMembers}
                                    totalCount={teamMembers.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex: number, item: TeamMember) => (
                                        <ComboboxItem
                                            hasItemCheckbox
                                            key={itemIndex}
                                            value={item.id}
                                            disabled={disabledConsultants?.includes(item.user.id)}
                                        >
                                            {item.user.fullName}
                                        </ComboboxItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </ComboboxPopover>
                </ComboboxProvider>
            </Flexbox>
        )
    }
)
